// Classifies the mentor turn into chat/knowledge/brainstorm/mentor before any
// RAG fetch or card/brief logic runs. Every turn goes through the LLM
// classifier — the previous rule-based fast paths were dropped because they
// kept misrouting real conversation (e.g. a clarifying question like "cosa
// significa X?" landing in "brainstorm" and losing the chat history). A
// dedicated LLM call is slower but far more precise. Fallback is "mentor"
// (not "chat") — matches the cloud IntentRouterNode's own
// "unrecognized -> mentor" policy: failing toward the guided flow is safer
// than silently dropping into chitchat and never engaging the process.
import { renderStr } from "./prompts";

export const INTENTS = ["chat", "knowledge", "brainstorm", "mentor"];

// Modes whose board mechanic IS the mentor flow itself (the mind map, the
// branching scenario timeline). The "brainstorm" intent exists to answer
// "analizza le carte sul tavolo" with a one-off Pattern/Prossime-mosse/
// Domande report, and it early-returns from the mentor pipeline entirely —
// which in these modes silently skips mode handling, the scenario-action
// pass, and therefore every fork and card for that turn. A single
// misclassified message (and the classifier does misread rich, on-topic
// project talk as "brainstorm" — observed live) was enough to make the
// timeline look permanently broken. In these modes a brainstorm-flavoured
// turn is just a mentor turn.
export const STRUCTURED_BOARD_MODES = ["mappatura", "scenari_futuri"];

export function allowBrainstormIntent(mode) {
  return !STRUCTURED_BOARD_MODES.includes(mode);
}

// The intent a turn should actually run as, given the session's mode.
export function resolveIntentForMode(intent, mode) {
  return intent === "brainstorm" && !allowBrainstormIntent(mode) ? "mentor" : intent;
}

// Finds which candidate appears earliest in `raw`, rather than which one is
// declared first in `candidates`. A garbled model response can mention more
// than one valid word; the one the model actually meant is the one it wrote
// first, not whichever happens to be first in our list.
function firstMatchByPosition(raw, candidates) {
  let best = null;
  let bestIndex = Infinity;
  for (const c of candidates) {
    const m = new RegExp(`\\b${c}\\b`, "i").exec(raw || "");
    if (m && m.index < bestIndex) {
      best = c;
      bestIndex = m.index;
    }
  }
  return best;
}

// Both local transports force response_format: json_object at the grammar
// level regardless of what the prompt asks for (see webllmClient.js's
// generateJSON, and engine/host/inference.py's Engine.chat, which sets
// {"type": "json_object"} unconditionally, schema or not) — so a prompt
// asking for "just answer one word" was always fighting that constraint,
// relying on the target word merely APPEARING somewhere in whatever
// JSON-ish text came out. This version asks for JSON explicitly instead,
// and — the actual fix for the reported bug — asks for a one-sentence
// reasoning before the tag: a bare single-word answer to a 4-way,
// rule-cascaded classification is exactly the kind of judgment call a weak
// local model pattern-matches badly on ("chat" for a long, dense,
// project-content message that happens to end in a question), the same
// abstention-style failure mode fixed for scenario-action by making that
// call propose instead of judge. Reasoning first gives the model somewhere
// to work through the cascade before committing.
export function buildIntentClassifierPrompt(chat, lang = "it") {
  return renderStr("intent_classifier_js", lang, { chat });
}

// `generate` is a transport-agnostic (prompt -> Promise<string>) function
// injected by the caller — pass one with a token budget large enough for a
// short reasoning sentence plus the JSON wrapper (not the ~24-token budget
// the terse mode/checkpoint/move classifiers use).
export async function classifyIntent({ chat, generate, lang = "it" }) {
  try {
    const raw = await generate(buildIntentClassifierPrompt(chat, lang));
    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}") + 1;
    if (start !== -1 && end > 0) {
      try {
        const parsed = JSON.parse(raw.slice(start, end));
        const tagged = String(parsed.intent ?? parsed.INTENT ?? "").toLowerCase().trim();
        if (INTENTS.includes(tagged)) return tagged;
      } catch (e) {
        // fall through to the loose text scan below
      }
    }
    const found = firstMatchByPosition(raw, INTENTS);
    return found || "mentor";
  } catch (e) {
    return "mentor";
  }
}
